import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RoadPavement } from '../../../../../../services/types/Calculator';
import { Modal } from '../../../../../../shared/components';
import { handleKeyDown } from '../../Utils/KeyboardHandlers';
import BasicRadioButton from '../../../../../../shared/components/BasicRadioButton';
import CalculatorSectorContext from '../UrbanInfrastructureTool/CalculatorSectorContext';
import {
  calculateRoadPavement,
  calculateRoadPavementTransport,
} from '../../Utils/moduleCalculates';
import RoadPavementDetailsTransport from './Details/RoadPavementDetailsTransport';

import CalculatorTableWrapper from '../Shared/CalculatorTableWrapper';

interface Props {
  roadPavementTransport: RoadPavement;
  onUpdateRoadPavementTransport: (updatedRoadPavement: RoadPavement) => void;
}

const RoadPavementTransportSection: React.FC<Props> = ({
  roadPavementTransport,
  onUpdateRoadPavementTransport,
}) => {
  const { t } = useTranslation();

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [totalCarbonSum, setTotalCarbonSum] = useState<number | null>(null);
  const initialRadio =
    roadPavementTransport.isTotalAreaKnown !== null
      ? Number(roadPavementTransport.isTotalAreaKnown)
      : null;
  const [radioSelected, setRadioSelected] = useState<number | null>(initialRadio);
  const { measurementSystem } = useContext(CalculatorSectorContext);

  const factorEFPer1000SqFt = 17.0;
  const [expandedRowIndex, setExpandedRowIndex] = useState<number | null>(initialRadio);

  const handleRadioChange = (value: number) => {
    setRadioSelected(value);

    onUpdateRoadPavementTransport({
      ...roadPavementTransport,
      isTotalAreaKnown: value === 1,
      totalArea: null,
      pavedRoadLength: null,
      numberOfLanes: null,
      moduleId: roadPavementTransport.moduleId,
      subTotal: null,
      pavedRoadArea: null,
      totalAreaRoadRehabilitated: null,
    });

    setTotalCarbonSum(null);

    setExpandedRowIndex(value);
  };

  const getTextMeasure = (): string => (measurementSystem.id === 1 ? 'Meters' : 'Feet');

  const handleInputTotalArea = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value.length > 10) {
      return;
    }

    onUpdateRoadPavementTransport({
      ...roadPavementTransport,
      totalArea: value === '' ? null : Number(value),
    });
  };

  const handleInputPavedRoad = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value.length > 8) {
      return;
    }

    onUpdateRoadPavementTransport({
      ...roadPavementTransport,
      pavedRoadLength: value === '' ? null : Number(value),
    });
  };

  const handleInputNumberLanes = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value.length > 8) {
      return;
    }

    onUpdateRoadPavementTransport({
      ...roadPavementTransport,
      numberOfLanes: value === '' ? null : Number(value),
    });
  };

  const handleInputTotalAreaRoadRehabilitated = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value.length > 8) {
      return;
    }

    onUpdateRoadPavementTransport({
      ...roadPavementTransport,
      totalAreaRoadRehabilitated: value === '' ? null : Number(value),
    });
  };

  useEffect(() => {
    const areaKnown = roadPavementTransport.isTotalAreaKnown;
    const totalArea = Number(roadPavementTransport.totalArea);

    const roadLength = Number(roadPavementTransport.pavedRoadLength);
    const numberLanes = Number(roadPavementTransport.numberOfLanes);

    const totalAreaRoadRehabilitated = Number(roadPavementTransport.totalAreaRoadRehabilitated);

    if (areaKnown === null) {
      setTotalCarbonSum(null);
      onUpdateRoadPavementTransport({
        ...roadPavementTransport,
        isTotalAreaKnown: null,
        totalArea: null,
        pavedRoadLength: null,
        numberOfLanes: null,
        moduleId: roadPavementTransport.moduleId,
        subTotal: null,
        pavedRoadArea: null,
        totalAreaRoadRehabilitated: null,
      });
    } else if (areaKnown && totalArea && roadPavementTransport.totalArea) {
      const factor = factorEFPer1000SqFt / roadPavementTransport.sqFeetToSqMeters;
      let result = calculateRoadPavement(factor, totalArea);

      if (
        roadPavementTransport.totalAreaRoadRehabilitated &&
        roadPavementTransport.sqDefaultEmission
      ) {
        const SqEmissionFactor =
          roadPavementTransport.sqDefaultEmission / roadPavementTransport.sqFeetToSqMeters;
        result = calculateRoadPavementTransport(
          result,
          roadPavementTransport.totalAreaRoadRehabilitated,
          SqEmissionFactor
        );
      }

      onUpdateRoadPavementTransport({
        ...roadPavementTransport,
        totalArea,
        moduleId: 0,
        subTotal: result,
        totalAreaRoadRehabilitated:
          totalAreaRoadRehabilitated === null || totalAreaRoadRehabilitated === 0
            ? null
            : totalAreaRoadRehabilitated,
      });

      setTotalCarbonSum(result);
    } else if (
      (areaKnown === false && roadLength) ||
      roadPavementTransport.pavedRoadLength ||
      numberLanes ||
      roadPavementTransport.numberOfLanes ||
      totalAreaRoadRehabilitated ||
      roadPavementTransport.totalAreaRoadRehabilitated
    ) {
      // meters
      const factor = factorEFPer1000SqFt / roadPavementTransport.sqFeetToSqMeters;
      const resultingRoadlen = numberLanes * roadPavementTransport.factorWidth;
      const pavedRoad = roadLength * 1000;
      const areaCalculated = resultingRoadlen * pavedRoad;

      let result = calculateRoadPavement(factor, areaCalculated);

      if (
        roadPavementTransport.totalAreaRoadRehabilitated &&
        roadPavementTransport.sqDefaultEmission
      ) {
        const SqEmissionFactor =
          roadPavementTransport.sqDefaultEmission / roadPavementTransport.sqFeetToSqMeters;
        result = calculateRoadPavementTransport(
          result,
          roadPavementTransport.totalAreaRoadRehabilitated,
          SqEmissionFactor
        );
      }

      onUpdateRoadPavementTransport({
        ...roadPavementTransport,
        totalArea: null,
        pavedRoadLength: roadLength === null || roadLength === 0 ? null : roadLength,
        numberOfLanes: numberLanes === null || numberLanes === 0 ? null : numberLanes,
        moduleId: 0,
        subTotal: result,
        pavedRoadArea: areaCalculated === null || areaCalculated === 0 ? null : areaCalculated,
        totalAreaRoadRehabilitated:
          totalAreaRoadRehabilitated === null || totalAreaRoadRehabilitated === 0
            ? null
            : totalAreaRoadRehabilitated,
      });

      setTotalCarbonSum(result);
    } else {
      setTotalCarbonSum(null);
      onUpdateRoadPavementTransport({
        ...roadPavementTransport,
        totalArea: null,
        pavedRoadLength: roadPavementTransport.pavedRoadLength,
        numberOfLanes: roadPavementTransport.numberOfLanes,
        moduleId: roadPavementTransport.moduleId,
        subTotal: null,
        pavedRoadArea: null,
        totalAreaRoadRehabilitated: null,
      });
    }
  }, [
    roadPavementTransport.isTotalAreaKnown,
    roadPavementTransport.totalArea,
    roadPavementTransport.pavedRoadLength,
    roadPavementTransport.numberOfLanes,
    roadPavementTransport.pavedRoadArea,
    roadPavementTransport.totalAreaRoadRehabilitated,
  ]);

  return (
    <>
      <div className="row calculator-title-row">
        <div className="radio-calculator">
          <BasicRadioButton
            id="rb_road"
            name="roadspaved_area"
            label="Enter the total area of roads paved, if known."
            radioValue={1}
            active={radioSelected === 1}
            onChange={(value) => handleRadioChange(value)}
          />
          <BasicRadioButton
            id="rb_road"
            name="roadspaved_calculated"
            label="If the total area of roads paved in the project is not known, enter the length of all roads paved and the number of lanes paved. "
            radioValue={0}
            active={radioSelected === 0}
            onChange={(value) => handleRadioChange(value)}
          />
        </div>
      </div>
      <br />
      {expandedRowIndex === 1 && (
        <div className="row calculator-input-row">
          <div className="col-12 col-sm-5 col-md-3 col-lg-2">
            <label className="col-form-label mr-2" htmlFor="sector">
              {t('Square ') + getTextMeasure()}
            </label>
          </div>
          <div className="col-12 col-sm-5 col-md-4 col-lg-2">
            <input
              title="Square Meters"
              type="number"
              id="totalAreaInput"
              value={
                roadPavementTransport.totalArea !== null ? roadPavementTransport.totalArea : ''
              }
              onChange={handleInputTotalArea}
              min="0"
              max={99999999.99}
              maxLength={10}
              onKeyDown={handleKeyDown}
              className="calculator-input"
            />
          </div>
        </div>
      )}

      {expandedRowIndex === 0 && (
        <div className="row calculator-input-row">
          <div className="col-12 col-sm-7 col-md-3 col-lg-2">
            <label className="col-form-label mr-2" htmlFor="sector">
              {t('Paved Road Length (Kilometers)')}
            </label>
          </div>
          <div className="col-12 col-sm-5 col-md-3 col-lg-2">
            <input
              title="Paved Road Length (Kilometers)"
              type="number"
              id="roadLengthInput"
              value={
                roadPavementTransport.pavedRoadLength !== null
                  ? roadPavementTransport.pavedRoadLength
                  : ''
              }
              onChange={handleInputPavedRoad}
              min="0"
              max={999999.99}
              maxLength={8}
              onKeyDown={handleKeyDown}
              className="calculator-input"
            />
          </div>

          <div className="col-12 col-sm-7 col-md-3 col-lg-2">
            <label className="col-form-label mr-2" htmlFor="sector">
              {t('Number of Lanes')}
            </label>
          </div>
          <div className="col-12 col-sm-5 col-md-3 col-lg-2">
            <input
              title="Number of Lanes"
              type="number"
              id="numberLanesInput"
              value={
                roadPavementTransport.numberOfLanes !== null
                  ? roadPavementTransport.numberOfLanes
                  : ''
              }
              onChange={handleInputNumberLanes}
              min="0"
              max={999999.99}
              maxLength={8}
              onKeyDown={handleKeyDown}
              className="calculator-input"
            />
          </div>

          <div className="col-12 col-sm-7 col-md-3 col-lg-2">
            <label className="col-form-label mr-2" htmlFor="sector">
              {t('Paved Road sq meters')}
            </label>
          </div>
          <div className="col-12 col-sm-5 col-md-3 col-lg-2">
            <input
              title="Paved Road sq meters"
              type="number"
              id="pavedRoadAreaInput"
              value={
                roadPavementTransport.pavedRoadArea !== null
                  ? roadPavementTransport.pavedRoadArea
                  : ''
              }
              className="calculator-input"
              disabled
            />
          </div>
        </div>
      )}

      {expandedRowIndex === 0 || expandedRowIndex === 1 ? (
        <div className="row calculator-input-row">
          <div className="col-12 col-sm-7 col-md-6 col-lg-4 col-xl-3">
            <label className="col-form-label mr-2" htmlFor="sector">
              {t('Total Area of Road rehabilitated, but not paved')}
            </label>
          </div>
          <div className="col-12 col-sm-5 col-md-3 col-lg-2 col-xl-2">
            <input
              title="Total Area of Road rehabilitated, but not paved"
              type="number"
              id="totalAreaRoadRehabilitatedInput"
              value={
                roadPavementTransport.totalAreaRoadRehabilitated !== null
                  ? roadPavementTransport.totalAreaRoadRehabilitated
                  : ''
              }
              onChange={handleInputTotalAreaRoadRehabilitated}
              min="0"
              max={999999.99}
              maxLength={8}
              onKeyDown={handleKeyDown}
              className="calculator-input"
            />
          </div>
        </div>
      ) : null}

      <CalculatorTableWrapper
        totalCarbonSum={totalCarbonSum}
        onShowDetailsModal={() => setShowDetailsModal(true)}
        subtotalColSpan={3}
        colGroup={
          <colgroup>
            <col style={{ width: '40%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
        }
      />
      <Modal
        title={roadPavementTransport.name}
        visible={showDetailsModal}
        setVisible={() => setShowDetailsModal(false)}
        actions={[{ text: 'Close', action: () => setShowDetailsModal(false) }]}
        customClass="extra_wide-modal"
        onClosed={() => setShowDetailsModal(false)}
      >
        <div className="mb-0 table-responsive">
          <RoadPavementDetailsTransport roadPavement={roadPavementTransport} />
        </div>
      </Modal>
    </>
  );
};

export default RoadPavementTransportSection;
